
import React, { useState } from "react";
import { Shop } from "@/components/dashboard/shops/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Pencil, Instagram, Facebook } from "lucide-react";

interface SocialMediaSectionProps {
  shop: Shop;
}

export const SocialMediaSection: React.FC<SocialMediaSectionProps> = ({ shop }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [instagramUrl, setInstagramUrl] = useState(shop.instagram_url || "");
  const [instagramUsername, setInstagramUsername] = useState(shop.instagram_username || "");
  const [facebookUrl, setFacebookUrl] = useState(shop.facebook_url || "");
  const [facebookUsername, setFacebookUsername] = useState(shop.facebook_username || "");
  const [tiktokUrl, setTiktokUrl] = useState(shop.tiktok_url || "");
  const [tiktokUsername, setTiktokUsername] = useState(shop.tiktok_username || "");
  
  const handleSave = () => {
    // Here, you would implement the logic to save the changes
    // to the Supabase database
    
    // For now, we'll simply display a success toast
    toast.success("Modifications enregistrées avec succès", {
      description: "Les liens des réseaux sociaux ont été mis à jour."
    });
    
    setIsEditing(false);
  };
  
  return (
    <div className="md:col-span-2">
      <Card className="shadow-md border-0 dark:border dark:border-gray-800">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle>Réseaux Sociaux</CardTitle>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setIsEditing(!isEditing)}
            className="flex items-center gap-1"
          >
            <Pencil className="h-4 w-4" />
            {isEditing ? "Annuler" : "Modifier"}
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="instagram-url" className="flex items-center gap-2">
                <Instagram size={16} className="text-pink-500" />
                Lien Instagram
              </Label>
              {isEditing ? (
                <div className="flex flex-col gap-2">
                  <Input 
                    id="instagram-url" 
                    value={instagramUrl} 
                    onChange={(e) => setInstagramUrl(e.target.value)} 
                    placeholder="https://www.instagram.com/"
                  />
                  <Input 
                    id="instagram-username" 
                    value={instagramUsername} 
                    onChange={(e) => setInstagramUsername(e.target.value)} 
                    placeholder="Nom d'utilisateur Instagram"
                  />
                </div>
              ) : (
                <div className="text-gray-600 dark:text-gray-400">
                  {instagramUrl ? (
                    <a 
                      href={instagramUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:underline flex items-center gap-2"
                    >
                      {instagramUrl}
                      {instagramUsername && <span className="text-gray-500">({instagramUsername})</span>}
                    </a>
                  ) : (
                    "Aucun lien Instagram défini"
                  )}
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="facebook-url" className="flex items-center gap-2">
                <Facebook size={16} className="text-blue-600" />
                Lien Facebook
              </Label>
              {isEditing ? (
                <div className="flex flex-col gap-2">
                  <Input 
                    id="facebook-url" 
                    value={facebookUrl} 
                    onChange={(e) => setFacebookUrl(e.target.value)} 
                    placeholder="https://www.facebook.com/"
                  />
                  <Input 
                    id="facebook-username" 
                    value={facebookUsername} 
                    onChange={(e) => setFacebookUsername(e.target.value)} 
                    placeholder="Nom d'utilisateur Facebook"
                  />
                </div>
              ) : (
                <div className="text-gray-600 dark:text-gray-400">
                  {facebookUrl ? (
                    <a 
                      href={facebookUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:underline flex items-center gap-2"
                    >
                      {facebookUrl}
                      {facebookUsername && <span className="text-gray-500">({facebookUsername})</span>}
                    </a>
                  ) : (
                    "Aucun lien Facebook défini"
                  )}
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="tiktok-url" className="flex items-center gap-2">
                <svg 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-black dark:text-white"
                >
                  <path 
                    d="M17 3v5.4c-.9-.534-1.933-.8-3.1-.8-3.45 0-6.25 2.8-6.25 6.25 0 3.45 2.8 6.25 6.25 6.25 3.45 0 6.25-2.8 6.25-6.25V3h-3.15zM13.9 17.5c-2 0-3.65-1.65-3.65-3.65 0-2 1.65-3.65 3.65-3.65.867 0 1.633.3 2.233.867v5.566c-.6.567-1.366.867-2.233.867z" 
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Lien TikTok
              </Label>
              {isEditing ? (
                <div className="flex flex-col gap-2">
                  <Input 
                    id="tiktok-url" 
                    value={tiktokUrl} 
                    onChange={(e) => setTiktokUrl(e.target.value)} 
                    placeholder="https://www.tiktok.com/@"
                  />
                  <Input 
                    id="tiktok-username" 
                    value={tiktokUsername} 
                    onChange={(e) => setTiktokUsername(e.target.value)} 
                    placeholder="Nom d'utilisateur TikTok"
                  />
                </div>
              ) : (
                <div className="text-gray-600 dark:text-gray-400">
                  {tiktokUrl ? (
                    <a 
                      href={tiktokUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:underline flex items-center gap-2"
                    >
                      {tiktokUrl}
                      {tiktokUsername && <span className="text-gray-500">({tiktokUsername})</span>}
                    </a>
                  ) : (
                    "Aucun lien TikTok défini"
                  )}
                </div>
              )}
            </div>
            
            {isEditing && (
              <div className="flex justify-end pt-4">
                <Button onClick={handleSave}>Enregistrer les modifications</Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
